'use strict';

/** @type {Promise<void>|null} */
let apiLoaderPromise = null;

/** @type {string|null} */
let loadedApiKey = null;

/** @type {boolean} */
let consentListenersRegistered = false;

const DEFAULT_ZOOM = 15;
const MIN_ZOOM = 0;
const MAX_ZOOM = 22;

/**
 * @param {number} zoom
 * @returns {number}
 */
function normalizeZoom(zoom) {
    if (Number.isNaN(zoom)) {
        return DEFAULT_ZOOM;
    }

    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(zoom)));
}

/**
 * @param {number} lat
 * @param {number} lng
 * @param {number} zoom
 * @returns {string}
 */
function buildGoogleMapsUrl(lat, lng, zoom) {
    const params = new URLSearchParams({
        api: '1',
        query: `${lat},${lng}`,
        zoom: String(normalizeZoom(zoom)),
    });

    return `https://www.google.com/maps/search/?${params.toString()}`;
}

/**
 * @param {number} lat
 * @param {number} lng
 * @param {number} zoom
 */
function openGoogleMaps(lat, lng, zoom) {
    window.open(buildGoogleMapsUrl(lat, lng, zoom), '_blank', 'noopener,noreferrer');
}

/**
 * @param {string} apiKey
 * @returns {Promise<void>}
 */
function loadGoogleMapsApi(apiKey) {
    if (window.google?.maps?.importLibrary && loadedApiKey === apiKey) {
        return Promise.resolve();
    }

    if (apiLoaderPromise && loadedApiKey === apiKey) {
        return apiLoaderPromise;
    }

    loadedApiKey = apiKey;
    apiLoaderPromise = new Promise((resolve, reject) => {
        ((g) => {
            var h;
            var a;
            var k;
            var p = 'The Google Maps JavaScript API';
            var c = 'google';
            var l = 'importLibrary';
            var q = '__ib__';
            var m = document;
            var b = window;
            b = b[c] || (b[c] = {});
            var d = b.maps || (b.maps = {});
            var r = new Set();
            var e = new URLSearchParams();
            var u = () =>
                h ||
                (h = new Promise(async (f, n) => {
                    await (a = m.createElement('script'));
                    e.set('libraries', [...r] + '');
                    for (k in g) {
                        e.set(k.replace(/[A-Z]/g, (t) => '_' + t[0].toLowerCase()), g[k]);
                    }
                    e.set('callback', c + '.maps.' + q);
                    a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
                    d[q] = f;
                    a.onerror = () => n(Error(p + ' could not load.'));
                    a.nonce = m.querySelector('script[nonce]')?.nonce || '';
                    m.head.append(a);
                }));
            d[l]
                ? console.warn(p + ' only loads once. Ignoring:', g)
                : (d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n)));
        })({
            key: apiKey,
            v: 'weekly',
        });

        window.google.maps
            .importLibrary('maps')
            .then(() => resolve())
            .catch(reject);
    });

    return apiLoaderPromise;
}

/**
 * @param {HTMLElement} element
 * @returns {{ background: string, border: string, glyph: string }|null}
 */
function getMarkerColors(element) {
    const section = element.closest('.content-section');

    if (!section) {
        return null;
    }

    const styles = getComputedStyle(section);
    const background = styles.getPropertyValue('--jf-google-maps-marker-background').trim();

    if (background === '') {
        return null;
    }

    return {
        background,
        border: styles.getPropertyValue('--jf-google-maps-marker-border').trim(),
        glyph: styles.getPropertyValue('--jf-google-maps-marker-glyph').trim(),
    };
}

/**
 * @param {google.maps.Map} map
 * @param {{ lat: number, lng: number }} position
 * @param {number} zoom
 * @param {string} mapId
 * @param {{ background: string, border: string, glyph: string }|null} markerColors
 * @returns {Promise<void>}
 */
async function addMarker(map, position, zoom, mapId, markerColors) {
    const onMarkerClick = () => openGoogleMaps(position.lat, position.lng, zoom);

    if (mapId !== '') {
        const { AdvancedMarkerElement, PinElement } = await window.google.maps.importLibrary('marker');

        if (markerColors !== null) {
            const pin = new PinElement({
                background: markerColors.background,
                borderColor: markerColors.border,
                glyphColor: markerColors.glyph,
            });
            const marker = new AdvancedMarkerElement({
                map,
                position,
                content: pin.element,
                gmpClickable: true,
            });
            marker.addEventListener('gmp-click', onMarkerClick);
            return;
        }

        const marker = new AdvancedMarkerElement({
            map,
            position,
            gmpClickable: true,
        });
        marker.addEventListener('gmp-click', onMarkerClick);
        return;
    }

    if (markerColors !== null) {
        const marker = new window.google.maps.Marker({
            map,
            position,
            clickable: true,
            icon: {
                path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
                fillColor: markerColors.background,
                fillOpacity: 1,
                strokeColor: markerColors.border,
                strokeWeight: 1.5,
                scale: 1.6,
                anchor: new window.google.maps.Point(12, 22),
            },
        });
        marker.addListener('click', onMarkerClick);
        return;
    }

    const marker = new window.google.maps.Marker({
        map,
        position,
        clickable: true,
    });
    marker.addListener('click', onMarkerClick);
}

/**
 * @param {HTMLElement} host
 * @returns {{ required: boolean, cookieName: string, consentName: string, dimension: string }}
 */
function readConsentConfig(host) {
    return {
        required: host.dataset.consentRequired === 'true',
        cookieName: host.dataset.consentCookie || 'KD_GDPR_CC',
        consentName: host.dataset.consentName || 'googleMaps',
        dimension: host.dataset.consentDimension || 'default',
    };
}

/**
 * @param {string} cookieName
 * @returns {object|null}
 */
function readConsentCookie(cookieName) {
    const cookiePrefix = `${cookieName}=`;
    const cookiePart = document.cookie
        .split('; ')
        .find((entry) => entry.startsWith(cookiePrefix));

    if (!cookiePart) {
        return null;
    }

    const rawValue = cookiePart.slice(cookiePrefix.length);

    try {
        return JSON.parse(decodeURIComponent(rawValue));
    } catch (error) {
        try {
            return JSON.parse(decodeURI(rawValue));
        } catch (nestedError) {
            return null;
        }
    }
}

/**
 * @param {{ required: boolean, cookieName: string, consentName: string, dimension: string }} config
 * @returns {boolean}
 */
function isConsentGranted(config) {
    if (!config.required) {
        return true;
    }

    const cookie = readConsentCookie(config.cookieName);

    if (!cookie || !cookie.consents) {
        return false;
    }

    const consents = cookie.consents[config.dimension] ?? cookie.consents.default ?? cookie.consents;

    return Array.isArray(consents) && consents.includes(config.consentName);
}

/**
 * @param {HTMLElement} host
 * @returns {boolean}
 */
function isHostConsentGranted(host) {
    return isConsentGranted(readConsentConfig(host));
}

/**
 * @param {HTMLElement} element
 * @returns {Promise<void>}
 */
async function initMapElement(element) {
    if (element.dataset.googleMapsInitialized === 'true') {
        return;
    }

    const apiKey = element.dataset.apiKey;
    const lat = Number.parseFloat(element.dataset.lat || '');
    const lng = Number.parseFloat(element.dataset.lng || '');
    const mapId = element.dataset.mapId || '';
    const zoom = normalizeZoom(Number.parseInt(element.dataset.zoom || String(DEFAULT_ZOOM), 10));

    if (!apiKey || Number.isNaN(lat) || Number.isNaN(lng)) {
        return;
    }

    await loadGoogleMapsApi(apiKey);

    const { Map } = await window.google.maps.importLibrary('maps');
    const position = { lat, lng };
    const mapOptions = {
        center: position,
        zoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
    };

    if (mapId !== '') {
        mapOptions.mapId = mapId;
    }

    const map = new Map(element, mapOptions);
    await addMarker(map, position, zoom, mapId, getMarkerColors(element));
    element.dataset.googleMapsInitialized = 'true';
}

/**
 * @param {HTMLElement} host
 */
function destroyMapsInHost(host) {
    host.querySelectorAll('[data-google-maps="true"]').forEach((element) => {
        delete element.dataset.googleMapsInitialized;
        element.replaceChildren();
    });
}

/**
 * @param {HTMLElement} host
 */
function applyHostConsentState(host) {
    const allowed = isHostConsentGranted(host);

    host.classList.toggle('is-consent-granted', allowed);
    host.classList.toggle('is-consent-denied', !allowed);

    if (!allowed) {
        destroyMapsInHost(host);
        return;
    }

    host.querySelectorAll('[data-google-maps="true"]:not([data-google-maps-initialized="true"])').forEach((element) => {
        initMapElement(element).catch((error) => {
            console.error('Google Maps initialization failed.', error);
        });
    });
}

/**
 * @param {() => void} callback
 */
function registerConsentListeners(callback) {
    if (consentListenersRegistered) {
        return;
    }

    consentListenersRegistered = true;

    document.addEventListener('GDPR-CC-consent', callback);

    window.dataLayer = window.dataLayer || [];

    if (!window.dataLayer.__jfGoogleMapsHooked) {
        const originalPush = window.dataLayer.push.bind(window.dataLayer);

        window.dataLayer.push = function pushWithConsentHook(...args) {
            const result = originalPush(...args);

            for (const arg of args) {
                if (arg?.event === 'KD_GDPR_CC_consent') {
                    callback();
                }
            }

            return result;
        };

        window.dataLayer.__jfGoogleMapsHooked = true;
    }
}

function applyAllHostsConsentState() {
    document.querySelectorAll('[data-google-maps-host="true"]').forEach((host) => {
        applyHostConsentState(host);
    });
}

export function initGoogleMaps() {
    const hosts = document.querySelectorAll('[data-google-maps-host="true"]');

    if (hosts.length === 0) {
        return;
    }

    registerConsentListeners(applyAllHostsConsentState);
    applyAllHostsConsentState();
}
